import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import { Core } from '../core/Core';
import { LoaderController } from '../core/LoaderService';
import { CharacterMesh } from './CharacterMesh';
import { DebugService } from '../core/DebugService';
import { TweenService } from '../core/TweenService';
import { CameraAngleName } from '../data/CameraAngles';
import { BridgeActionResult, BridgeErrorStrings } from '../data/BridgeData';
import { CharacterAnimatorService } from './CharacterAnimatorService';
import idling from '../../static/assets/animations/characterDefault/idling.json';
import eyeLashesClosed from '../../static/assets/animations/characterDefault/eyeLashesClosed.json';
import eyeLidsClosed from '../../static/assets/animations/characterDefault/eyelidsClosed.json';

export class CharacterService {
	/**
	 * Core
	 */
	core: Core;
	characterGroup?: THREE.Group;
	character?: any;
	characterLookAtPosition = new THREE.Vector3(0, 0.75, 0);
	characterScene?: THREE.Scene;
	applyShadows: boolean = false;
	characterMeshOpacity: number = 0;

	characterMirrored: boolean = false;
	characterEyesClosed: boolean = false;
	characterLoaded: boolean = false;
	onCharacterLoadedCallback: Function[] = [];

	tShirtTexture?: any;
	tShirtTextureFlipped?: any;

	/**
	 * Mesh references
	 */
	meshes: CharacterMesh[] = [];
	transparentMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 });

	/**
	 * Constructor
	 * */
	constructor(core: Core) {
		this.core = core;

		this.loadCharacter();

		if (core.debugMode) {
			const sDebug: DebugService = core.s('Debug');
			const folder = sDebug.lilGUI!.addFolder('Texturing').open(false);
			folder.add(this, 'showFeetOnly').name('Feet');
			folder.add(this, 'setInitialMaterials').name('All');
		}
	}

	/**
	 * Load the character
	 */
	async loadCharacter() {
		const sLoader: LoaderController = this.core.s('Loader');

		// Load texture files
		this.tShirtTexture = await sLoader.loadTexture('assets/textures/texture.tshirt.jpg')!;
		this.tShirtTextureFlipped = await sLoader.loadTexture('assets/textures/texture.tshirt.flipped.jpg')!;

		const character: any = await sLoader.loadModelFromAsset('assets/st_character.glb');

		this.createThreeAssets(character);

		// Make custom textures to change the look and feel
		const blackPupils = new THREE.MeshBasicMaterial({ color: 'black' });
		character.scene.traverse((item: any) => {
			/*if (item.isMesh) {
				console.log(item);
			}*/

			// Pupils
			if (item.name.includes('Eyes')) {
				item.children.forEach((child: any) => {
					if (child.name.includes("_")) child.material = blackPupils;
				});
				return;
			}

			// Main skin
			if (["head001", "head001_2", "Geo_Male_White_Ears001"].includes(item.name)) {
				//const material = new THREE.MeshStandardMaterial({ color: '#D78975' }); // Peach
				const material = new THREE.MeshStandardMaterial({ color: '#bf7a69' }); // Dark/Light
				//const material = new THREE.MeshStandardMaterial({ color: '#8F4D2D' }); // Dark
				item.material = material;
			}

			// Hair
			if (["Geo_Male_White_Eyebrow001", "Geo_Male_White_Hair001"].includes(item.name)) {
				const material = new THREE.MeshStandardMaterial({ color: '#422C0E' }); // Dark
				//const material = new THREE.MeshStandardMaterial({ color: '#111111' }); // Black
				//const material = new THREE.MeshStandardMaterial({ color: '#BA9C69' }); // Sandy
				item.material = material;
			}

			// Laces
			if (["Geo_Male_White_White_shoes_lace001"].includes(item.name)) {
				const material = new THREE.MeshStandardMaterial({ color: '#999999' });
				item.material = material;
			}

			// Shirt
			if (item.name == 'Geo_Male_White_T-Shirt001') {
				const material = new THREE.MeshStandardMaterial({ color: 0x0F7669 });
				item.material = material;
			}
		});

		// Store references to the Meshes for texturing later
		character.scene.traverse((item: any) => {
			if (item.isMesh) {
				this.meshes.push(new CharacterMesh(item));
			}

			if (item.isMesh && this.applyShadows) {
				item.receiveShadow = true
				item.castShadow = true
			}
		});

		this.fadeInCharacter(2200);

		// Call any characterLoaded event callbacks
		this.characterLoaded = true;
		if (this.onCharacterLoadedCallback.length) {

			const sCharacterAnimator: CharacterAnimatorService = this.core.s('CharacterAnimator');
			sCharacterAnimator.setupCharacterAnimationMixer();

			// Setup default idle
			sCharacterAnimator.registerAnimation({
				id: 'Idle',
				json: idling,
				autoBlend: false,
				playbackSpeed: 1
			}, true);
			sCharacterAnimator.activateAnimation({
				id: 'Idle',
				weight: 1,
				fadeTimeMS: 100,
			});

			// Setup eyes closed
			sCharacterAnimator.registerAnimation({
				id: 'EyeLashesClosed',
				json: eyeLashesClosed,
				autoBlend: false,
				playbackSpeed: 1
			}, true);
			sCharacterAnimator.registerAnimation({
				id: 'EyeLashesClosedReversed',
				json: eyeLashesClosed,
				autoBlend: false,
				playbackSpeed: -1
			}, true);
			sCharacterAnimator.registerAnimation({
				id: 'EyeLidsClosed',
				json: eyeLidsClosed,
				autoBlend: false,
				playbackSpeed: 1
			}, true);
			sCharacterAnimator.registerAnimation({
				id: 'EyeLidsClosedReversed',
				json: eyeLidsClosed,
				autoBlend: false,
				playbackSpeed: -1
			}, true);


			this.onCharacterLoadedCallback.forEach(callback => callback());
		}
	}

	/**
	 * Creates the three assets required for the character
	 */
	createThreeAssets(character: any) {
		this.character = character;
		this.characterScene = character.scene;
		this.characterGroup = new THREE.Group();
		this.characterGroup.add(this.characterScene!);
		this.core.scene.add(this.characterGroup!);
	}

	/**
	 * Adds a callback for calling when the character loads, or calls it immediately if it already has loaded
	 * @param callback 
	 */
	registerOnCharacterLoadedCallback(callback: Function) {
		if (this.characterLoaded) callback();
		else this.onCharacterLoadedCallback.push(callback);
	}

	// Method that tries to use shaders
	/*animateDramaticEntrance() {
		this.meshes.forEach((mesh: any) => {

			console.log(mesh);

			if (mesh.threeMesh.material.map) {
				console.log(mesh.threeMesh.material);
				const texture = mesh.threeMesh.material.map;
				//item.material.flipY = false;
				//item.material.wrapS = texture.wrapT = ClampToEdgeWrapping;
				//item.material.flipY = true
				//item.material.encoding = THREE.sRGBEncoding;

				const customShaderMaterial = new THREE.ShaderMaterial({
					uniforms: {
						uTexture: { value: texture },
						uTime: { value: 0 },
					},
					vertexShader: textureColouriserVertex,
					fragmentShader: textureColouriserFragment,
				});

				mesh.threeMesh.material = customShaderMaterial;
			}
		});
	}*/

	/**
	 * Sets materials so that only the feet are showing
	 */
	showFeetOnly() {
		this.meshes.forEach(mesh => {
			if (['Geo_Male_White_White_shoes001', 'Geo_Male_White_White_shoes_lace001'].includes(mesh.threeMesh.name)) return;
			mesh.applyNewMaterial(this.transparentMaterial);
		});
	}

	/**
	 * Reverts to standard material
	 */
	setInitialMaterials() {
		this.meshes.forEach(mesh => {
			mesh.revertMaterial();
		});
	}

	/**
	 * Flips the character on the X axis to play animations left and right
	 */
	setFacing(isMirrored: boolean = true): BridgeActionResult {
		if (this.characterMirrored && isMirrored) return new BridgeActionResult(true);
		if (!this.characterMirrored && !isMirrored) return new BridgeActionResult(true);

		let texture;
		let scaleX;

		if (isMirrored) {
			scaleX = -1;
			texture = this.tShirtTextureFlipped;
		} else {
			scaleX = 1;
			texture = this.tShirtTexture;
		}

		const tShirtMesh = this.meshes.find(mesh => mesh.threeMesh.name == "Geo_Male_White_T-Shirt001");
		if (!tShirtMesh) return new BridgeActionResult(false, BridgeErrorStrings.no_asset);

		this.fadeOutCharacter(() => {
			texture.colorSpace = THREE.SRGBColorSpace;
			(tShirtMesh.threeMesh.material as THREE.Material).opacity = 0;
			(tShirtMesh.threeMesh.material as any).map = texture; // Any-ed to fix missing map type declaration
			(tShirtMesh.threeMesh.material as any).map.flipY = false; // Any-ed to fix missing map type declaration

			this.characterGroup?.scale.set(scaleX, 1, 1);

			this.fadeInCharacter();
		});

		this.characterMirrored = isMirrored;
		return new BridgeActionResult(true);
	}

	/**
	 * Runs eye animations, either forward or backward depending on isClosed
	 * @param isClosed
	 */
	setEyes(isClosed = true): BridgeActionResult {
		const sCharacterAnimator: CharacterAnimatorService = this.core.s('CharacterAnimator');

		if (isClosed) {
			if (this.characterEyesClosed) return new BridgeActionResult(true);
			sCharacterAnimator.activateAnimation({ id: "EyeLashesClosed", weight: 1, fadeTimeMS: 0, loopTimes: 1 });
			sCharacterAnimator.activateAnimation({ id: "EyeLidsClosed", weight: 1, fadeTimeMS: 0, loopTimes: 1 });
			this.characterEyesClosed = true;
		} else {
			if (!this.characterEyesClosed) return new BridgeActionResult(true);
			sCharacterAnimator.activateAnimation({ id: "EyeLashesClosedReversed", weight: 1, fadeTimeMS: 0, loopTimes: 1 });
			sCharacterAnimator.activateAnimation({ id: "EyeLidsClosedReversed", weight: 1, fadeTimeMS: 0, loopTimes: 1 });
			this.characterEyesClosed = false;
		}

		return new BridgeActionResult(true);
	}
	openEyes() { this.setEyes(false); }
	closeEyes() { this.setEyes(true); }

	/**
	 * Fades the character out
	 * @param onComplete An optional callback when the fade finishes
	 */
	fadeOutCharacter(onComplete?: Function): BridgeActionResult {
		this.meshes.forEach((mesh: any) => {
			mesh.threeMesh.material.opacity = 1;
			mesh.threeMesh.material.transparent = true;
			mesh.threeMesh.material.needsUpdate = true;
		});

		const sTween: TweenService = this.core.s('Tween');
		sTween.createTween({
			name: 'character_fadeout',
			targetObject: this,
			modifiedPropertyObject: { characterMeshOpacity: 0 },
			durationMS: 400,
			easing: TWEEN.Easing.Cubic.Out,
			onUpdate: () => {
				this.meshes.forEach(mesh => (mesh.threeMesh.material as THREE.Material).opacity = this.characterMeshOpacity)
			},
			onComplete: async () => {
				this.characterGroup!.visible = false;
				if (onComplete) onComplete();
			}
		});

		return new BridgeActionResult(true);
	}

	/**
	 * Fades in character
	 */
	fadeInCharacter(delay: number = 100): BridgeActionResult {
		this.meshes.forEach((mesh: any) => {
			mesh.threeMesh.material.opacity = 0;
			mesh.threeMesh.material.transparent = true;
			mesh.threeMesh.material.needsUpdate = true;
		});

		this.characterGroup!.visible = true;

		const sTween: TweenService = this.core.s('Tween');
		sTween.createTween({
			name: 'character_fadein',
			targetObject: this,
			modifiedPropertyObject: { characterMeshOpacity: 1 },
			durationMS: 750,
			easing: TWEEN.Easing.Cubic.Out,
			delayMS: delay,
			onUpdate: () => {
				this.meshes.forEach(mesh => (mesh.threeMesh.material as THREE.Material).opacity = this.characterMeshOpacity)
			},
			onComplete: async () => {
				// Remove transparency to reduce draw calls
				this.meshes.forEach((mesh: any) => {
					mesh.threeMesh.material.opacity = 1;
					mesh.threeMesh.material.transparent = false;
					mesh.threeMesh.material.needsUpdate = true;
				});
			}
		});

		return new BridgeActionResult(true);
	}

	/**
	 * Disposes a character
	 */
	dispose() {
		this.meshes.forEach(mesh => {
			mesh.threeMesh.geometry.dispose();
		});
		this.characterGroup?.removeFromParent();
	}
}

export interface CharacterAnimationRegistration {
	name: string;
	json: any;
	cameraAngle: CameraAngleName;
}